
import { useState } from "react";
import { useTranslations } from "use-intl";
import Link from "@/lib/next-shims/router-link";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Bot, Key, Cable, MessageSquare, Workflow as WorkflowIcon } from "lucide-react";
import { BenefitItem, PromptExample } from "@/components/shared/feature-announcements/shared";
import ConnectMcpModal from "@/components/shared/ConnectMcpModal";
import type { Workflow } from "@/types/workflow.types";

interface EditWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: unknown;
  isLoading?: boolean;
  workflow: Workflow | null;
}

export default function EditWorkflowModal({
  isOpen,
  onClose,
  workflow,
}: EditWorkflowModalProps) {
  const t = useTranslations("workflows");
  const tCommon = useTranslations("common");
  const [isMcpModalOpen, setIsMcpModalOpen] = useState(false);

  if (!workflow) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" aria-label={t("edit_ai_title")}>
        <ModalHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-linear-to-br from-brand-500 to-purple-600 text-white">
              <Bot className="size-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("edit_ai_title")}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {workflow.name}
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {/* Explanation */}
          <div className="rounded-xl bg-linear-to-br from-purple-50 to-violet-50 p-4 dark:from-purple-900/20 dark:to-violet-900/20">
            <p className="text-sm leading-relaxed text-purple-800 dark:text-purple-200">
              {t("edit_ai_desc")}
            </p>
          </div>

          {/* Benefits */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
              {t("edit_ai_benefits_title")}
            </h4>
            <ul className="space-y-1.5">
              <BenefitItem text={t("edit_ai_benefit_1")} />
              <BenefitItem text={t("edit_ai_benefit_2")} />
              <BenefitItem text={t("edit_ai_benefit_3")} />
              <BenefitItem text={t("edit_ai_benefit_4")} />
            </ul>
          </div>

          {/* Example prompts */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t("create_ai_examples_title")}
            </h4>
            <PromptExample
              icon={<MessageSquare className="h-4 w-4" />}
              title={t("edit_ai_example_1_title")}
              prompt={`Change workflow "${workflow.name}" to run every 2 hours instead of manually`}
              color="emerald"
            />
            <PromptExample
              icon={<WorkflowIcon className="h-4 w-4" />}
              title={t("edit_ai_example_2_title")}
              prompt={`Add a Lark notification step to "${workflow.name}" after the data load completes`}
              color="amber"
            />
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            {tCommon("cancel")}
          </Button>
          <div className="flex gap-2">
            <Link href="/my-api-keys" onClick={onClose}>
              <Button variant="outline">
                <Key className="mr-2 size-4" />
                {t("action_api_keys")}
              </Button>
            </Link>
            <Button
              onClick={() => {
                onClose();
                setIsMcpModalOpen(true);
              }}
            >
              <Cable className="mr-2 size-4" />
              {t("action_connect_mcp")}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <ConnectMcpModal
        isOpen={isMcpModalOpen}
        onClose={() => setIsMcpModalOpen(false)}
      />
    </>
  );
}
